import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Grid } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import logoImage from '../../assets/KimeraWare_logo.png';
import { NavigationItem } from './Header.types';
import {
  StyledHeader,
  HeaderContent,
  StyledFlex,
  HeaderLogo,
  HeaderLogoImg,
  DesktopNavigation,
  HeaderLanguageSelector,
  MobileMenuContainer,
} from './Header.styles';
import LanguageSelector from './LanguageSelector';
import HamburgerMenu, { HamburgerMenuIcon } from './HamburgerMenu';

const { useBreakpoint } = Grid;

const Header: React.FC = React.memo(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const skipLinkRef = useRef<HTMLAnchorElement>(null);
  const announcementRef = useRef<HTMLDivElement>(null);

  const navigationItems: NavigationItem[] = useMemo(
    () => [
      {
        key: 'home',
        label: t('header.home'),
        to: '/',
      },
      {
        key: 'games',
        label: t('header.games'),
        to: '/games',
      },
      {
        key: 'about',
        label: t('header.about'),
        to: '/about',
      },
    ],
    [t],
  );

  const activeItemKey = useMemo(() => {
    const currentPath = location.pathname;
    const activeItem = navigationItems.find((item) => item.to === currentPath);
    return activeItem ? activeItem.key : 'home';
  }, [location.pathname, navigationItems]);

  const announceToScreenReader = useCallback((message: string): void => {
    if (announcementRef.current) {
      announcementRef.current.textContent = message;
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = '';
        }
      }, 1000);
    }
  }, []);

  const handleNavigationClick = (to: string): void => {
    navigate(to);
  };

  const handleLogoKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigationClick('/');
    }
  };

  const handleMobileMenuToggle = (): void => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    const message = newState ? t('accessibility.menuOpened') : t('accessibility.menuClosed');
    announceToScreenReader(message);
  };

  const handleMobileMenuClose = useCallback((): void => {
    setIsMobileMenuOpen(false);
    announceToScreenReader(t('accessibility.menuClosed'));

    setTimeout(() => {
      const hamburgerButton = document.querySelector(
        '[aria-controls="mobile-navigation-menu"]',
      ) as HTMLElement;
      if (hamburgerButton) {
        hamburgerButton.focus();
        announceToScreenReader(t('accessibility.focusReturned'));
      }
    }, 100);
  }, [announceToScreenReader, t]);

  const handleSkipToMain = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.focus();
        mainContent.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        handleMobileMenuClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isMobileMenuOpen, handleMobileMenuClose]);

  const menuItems = useMemo(
    () =>
      navigationItems.map((item) => ({
        key: item.key,
        label: item.label,
        onClick: () => handleNavigationClick(item.to),
      })),
    [navigationItems],
  );

  const isMobile = !screens.md;

  return (
    <>
      <a
        ref={skipLinkRef}
        href="#main-content"
        onKeyDown={handleSkipToMain}
        className="skip-link"
        onFocus={(e) => {
          const target = e.target as HTMLAnchorElement;
          target.style.left = '6px';
          target.style.top = '6px';
        }}
        onBlur={(e) => {
          const target = e.target as HTMLAnchorElement;
          target.style.left = '-9999px';
          target.style.top = 'auto';
        }}
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          padding: '8px',
          background: '#000',
          color: '#fff',
          textDecoration: 'none',
        }}
      >
        {t('accessibility.skipToMainContent')}
      </a>

      <StyledHeader role="banner">
        <HeaderContent>
          <StyledFlex justify="space-between" align="center">
            <HeaderLogo>
              <HeaderLogoImg
                src={logoImage}
                alt={t('logoAlt')}
                onClick={() => handleNavigationClick('/')}
                onKeyDown={handleLogoKeyDown}
                role="button"
                tabIndex={0}
                aria-label={t('accessibility.navigateToHome')}
              />
            </HeaderLogo>

            {!isMobile && (
              <nav role="navigation" aria-label={t('accessibility.mainNavigation')}>
                <DesktopNavigation
                  mode="horizontal"
                  items={menuItems}
                  selectedKeys={[activeItemKey]}
                />
              </nav>
            )}

            <HeaderLanguageSelector>
              {!isMobile && <LanguageSelector />}
              {isMobile && (
                <MobileMenuContainer>
                  <HamburgerMenuIcon
                    onClick={handleMobileMenuToggle}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-navigation-menu"
                    aria-label={
                      isMobileMenuOpen
                        ? t('accessibility.closeMobileMenu')
                        : t('accessibility.openMobileMenu')
                    }
                  />
                </MobileMenuContainer>
              )}
            </HeaderLanguageSelector>
          </StyledFlex>
        </HeaderContent>
      </StyledHeader>

      <HamburgerMenu
        isOpen={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        navigationItems={navigationItems}
        onNavigationClick={handleNavigationClick}
        activeItemKey={activeItemKey}
      />

      <div
        ref={announcementRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />
    </>
  );
});

Header.displayName = 'Header';

export default Header;
